import React, { FC, useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Form, Message } from 'semantic-ui-react';

import { Align, Size, Uuid } from '@src/types';
import { useCurrentUser, useSystemToasts } from '@src/hooks';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { StyledContainer } from '@src/views/containers';

interface ExportSmartsheetDialogProps {
  projectVersionId: Uuid;
  onComplete?: (result: any) => void;
  onClose?: () => void;
}

const ExportSmartsheetDialog: FC<ExportSmartsheetDialogProps> = ({
  projectVersionId,
  onComplete,
  onClose,
}) => {
  const { currentUser } = useCurrentUser();
  const { exportSmartsheet } = useCentralCalls();
  const toastActions = useSystemToasts();

  const [ smartsheetToken, setSmartsheetToken ]           = useState( '' ),
        [ exportMode, setExportMode ]                     = useState<'create' | 'update'>('create'),
        [ sheetId, setSheetId ]                           = useState( '' ),
        [ newSheetName, setNewSheetName ]                 = useState( '' ),
        [ includeContainerRows, setIncludeContainerRows ] = useState( true );

  const [ isSubmitting, setIsSubmitting ] = useState( false ),
        [ error, setError ]               = useState<string | null>( null );

  const canSubmit = useMemo(() => {
    if (isSubmitting || smartsheetToken.trim().length === 0) {
      return false;
    }

    if (exportMode === 'update' && sheetId.trim().length === 0) {
      return false;
    }

    return true;
  }, [isSubmitting, smartsheetToken, exportMode, sheetId]);

  const handleSubmit = async () => {
    const accountId = currentUser?.id as string;

    if (!accountId) {
      setError('Current user account id is missing. Please refresh and try again.');
      return;
    }

    if (!projectVersionId) {
      setError('Project version id is missing. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await exportSmartsheet({
        SmartsheetToken: smartsheetToken.trim(),
        ProjectVersionId: String(projectVersionId),
        AccountId: accountId,
        ExportMode: exportMode,
        SheetId: exportMode === 'update' ? sheetId.trim() : undefined,
        NewSheetName: exportMode === 'create' ? (newSheetName.trim() || undefined) : undefined,
        IncludeContainerRows: includeContainerRows,
      });

      toastActions.success('Smartsheet export completed', 'Data has been exported to Smartsheet.');
      onComplete?.(result);
      onClose?.();
    } catch (ex) {
      const message = (ex as Error)?.message || 'Smartsheet export failed.';
      setError(message);
      toastActions.error('Smartsheet export failed', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasicDialogContainer
      title="Export Smartsheet"
      roundedTop
      contentPadding={[Size.MD, Size.MD, Size.LG, Size.MD]}
      onClose={onClose}
    >
      <Form>
        <Form.Field required>
          <label>Smartsheet API Key</label>
          <Form.Input
            type="password"
            placeholder="Smartsheet API token"
            value={smartsheetToken}
            onChange={(_, data) => setSmartsheetToken(data.value)}
            disabled={isSubmitting}
          />
        </Form.Field>

        <Form.Field required>
          <label>Export Mode</label>
          <Form.Select
            options={[
              { key: 'create', value: 'create', text: 'Create New Smartsheet' },
              { key: 'update', value: 'update', text: 'Update Existing Smartsheet' },
            ]}
            value={exportMode}
            onChange={(_, data) => setExportMode((data.value as 'create' | 'update') ?? 'create')}
            disabled={isSubmitting}
          />
        </Form.Field>

        {exportMode === 'create' && (
          <Form.Field>
            <label>New Smartsheet Name (optional)</label>
            <Form.Input
              placeholder="Leave blank for auto-generated name"
              value={newSheetName}
              onChange={(_, data) => setNewSheetName(data.value)}
              disabled={isSubmitting}
            />
          </Form.Field>
        )}

        {exportMode === 'update' && (
          <Form.Field required>
            <label>Smartsheet Sheet ID</label>
            <Form.Input
              placeholder="e.g. 1234567890123456"
              value={sheetId}
              onChange={(_, data) => setSheetId(data.value)}
              disabled={isSubmitting}
            />
          </Form.Field>
        )}

        <Form.Field>
          <Checkbox
            label="Include container rows"
            checked={includeContainerRows}
            onChange={(_, data) => setIncludeContainerRows(Boolean(data.checked))}
            disabled={isSubmitting}
          />
        </Form.Field>

        {error && (
          <Message negative>
            <Message.Content>{error}</Message.Content>
          </Message>
        )}
      </Form>

      <Divider />

      <StyledContainer textAlign={Align.RIGHT}>
        <Button
          type="button"
          basic
          content="Cancel"
          onClick={onClose}
        />
        <Button
          type="button"
          color="purple"
          content={isSubmitting ? 'Exporting...' : 'Export Smartsheet'}
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!canSubmit}
        />
      </StyledContainer>
    </BasicDialogContainer>
  );
};

export { ExportSmartsheetDialog };
